import { useEffect, useMemo, useState } from "react";

import { format, formatDistanceToNow, formatDistanceToNowStrict } from "date-fns";
import { es } from "date-fns/locale";
import {
  BookOpen,
  Check,
  Copy,
  LayoutDashboard,
  LogOut,
  Mail,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Users,
  UsersRound,
} from "lucide-react";

import { useSignOut } from "@/features/auth/application/use-sign-out";
import { PERMISSION_LABELS } from "@/features/auth/domain/admin-user";
import { BotanicalPattern } from "@/features/auth/presentation/components/BotanicalPattern";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import { useAppSelector } from "@/store";
import { Permission } from "@/store/apis/adminApi.enhanced";
import type { Admin } from "@/store/apis/adminApi.enhanced";
import { selectAdmin } from "@/store/selectors";

/**
 * "Identidad administrativa" — pantalla de cuenta como credencial oficial.
 *
 * Diseño editorial:
 *   1. Hero credencial → fondo forest + pattern botánico + grain + avatar
 *      con ring-glow. Credential strip con metadata (correo · rol · desde).
 *   2. Grid: Seguridad (compacto) + Scope de permisos (agrupado por área).
 *   3. Signature line al pie: ID administrativo + timestamp.
 *
 * Honesto con la data: no inventamos sesiones ni endpoints inexistentes.
 */
export function ProfilePage() {
  const admin = useAppSelector(selectAdmin);
  const { signOut, isLoading: signingOut } = useSignOut();

  if (!admin) return null;

  return (
    <div className="animate-fade-in-up space-y-5">
      <CredentialHero admin={admin} />
      <div className="grid gap-5 lg:grid-cols-[1fr_1.4fr]">
        <SecurityCard admin={admin} onSignOut={() => void signOut()} signingOut={signingOut} />
        <PermissionsCard admin={admin} />
      </div>
      <SignatureLine admin={admin} />
    </div>
  );
}

/* ────────────────── Credential Hero ────────────────── */

function CredentialHero({ admin }: { admin: Admin }) {
  const initials = getInitials(admin.full_name);
  const createdAt = admin.created_at ? new Date(admin.created_at) : null;

  return (
    <section
      aria-label="Credencial administrativa"
      className="relative overflow-hidden rounded-3xl bg-arya-forest text-arya-cream shadow-card-premium"
    >
      {/* Background layers */}
      <div
        aria-hidden
        className={cn(
          "absolute inset-0",
          "bg-[radial-gradient(ellipse_at_top_right,theme(colors.arya.leaf)_0%,transparent_55%),linear-gradient(135deg,#1E5E3A_0%,#2E7A55_55%,#235F43_100%)]",
        )}
      />
      <div aria-hidden className="bg-grain absolute inset-0 opacity-[0.14] mix-blend-overlay" />
      <div aria-hidden className="mask-fade-b absolute inset-0 text-arya-cream/[0.08]">
        <BotanicalPattern className="h-full w-full" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col gap-7 p-8 lg:p-10">
        {/* Top row: kicker + sello */}
        <div className="flex items-start justify-between">
          <p className="font-mono text-[10px] font-medium uppercase tracking-[0.28em] text-arya-cream/70">
            Identidad administrativa
          </p>
          <ActiveSeal active={admin.active} />
        </div>

        {/* Identity row */}
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
          {/* Avatar con ring glow */}
          <div className="relative">
            <div
              aria-hidden
              className="absolute inset-0 -m-2 rounded-full bg-gradient-to-br from-arya-mist/40 to-arya-cream/5 blur-lg"
            />
            <div
              aria-hidden
              className={cn(
                "relative flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl",
                "bg-gradient-to-br from-arya-cream to-arya-mist text-arya-forest",
                "shadow-xl ring-1 ring-arya-cream/40",
              )}
            >
              <span className="font-display text-[2rem] font-medium tracking-display-tight">
                {initials}
              </span>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <h1 className="font-display text-[2rem] font-medium leading-tight tracking-display-tight text-arya-cream lg:text-[2.4rem]">
              {admin.full_name}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-arya-cream/75">
              <span className="font-mono tracking-wide">{admin.role}</span>
              <span aria-hidden className="opacity-50">
                ·
              </span>
              <SessionDot />
            </div>
          </div>
        </div>

        {/* Credential strip */}
        <div className="rounded-2xl border border-arya-cream/15 bg-arya-cream/[0.04] px-5 py-4 backdrop-blur-sm">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field
              icon={<Mail className="h-3.5 w-3.5" />}
              label="Correo"
              value={<span className="font-mono text-[12px]">{admin.email}</span>}
            />
            <Field
              icon={<ShieldCheck className="h-3.5 w-3.5" />}
              label="Rol"
              value={<span className="text-[13px] font-medium">{admin.role}</span>}
            />
            <Field
              icon={<Sparkles className="h-3.5 w-3.5" />}
              label="Miembro desde"
              value={
                createdAt ? (
                  <span
                    className="text-[13px] font-medium"
                    title={format(createdAt, "d 'de' MMMM, yyyy", { locale: es })}
                  >
                    {format(createdAt, "MMM yyyy", { locale: es })}{" "}
                    <span className="font-normal text-arya-cream/55">
                      · hace {formatDistanceToNowStrict(createdAt, { locale: es })}
                    </span>
                  </span>
                ) : (
                  <span className="text-arya-cream/55">—</span>
                )
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ActiveSeal({ active }: { active: boolean }) {
  if (!active) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/15 px-2.5 py-1",
          "font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-arya-cream",
        )}
      >
        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-destructive" />
        Inactiva
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full border border-arya-cream/25 bg-arya-cream/10 px-3 py-1",
        "font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-arya-cream backdrop-blur-sm",
      )}
    >
      <span aria-hidden className="relative flex h-2 w-2">
        <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400 opacity-70" />
        <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
      </span>
      Cuenta verificada
    </span>
  );
}

function SessionDot() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className="relative flex h-1.5 w-1.5">
        <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400 opacity-60" />
        <span className="relative h-1.5 w-1.5 rounded-full bg-emerald-400" />
      </span>
      <span className="font-mono text-[11px] tabular-nums">
        Sesión activa · {format(now, "HH:mm")}
      </span>
    </span>
  );
}

function Field({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1 inline-flex items-center gap-1.5 font-mono text-[9.5px] font-medium uppercase tracking-[0.22em] text-arya-cream/55">
        {icon}
        {label}
      </p>
      <div className="truncate text-arya-cream">{value}</div>
    </div>
  );
}

/* ────────────────── Seguridad ────────────────── */

function SecurityCard({
  admin: _admin,
  onSignOut,
  signingOut,
}: {
  admin: Admin;
  onSignOut: () => void;
  signingOut: boolean;
}) {
  return (
    <section className="flex flex-col gap-5 rounded-2xl border bg-card p-6 shadow-card-premium">
      <header className="flex items-start gap-3">
        <div
          aria-hidden
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15"
        >
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-[1.15rem] font-medium tracking-display-tight text-foreground">
            Seguridad
          </h2>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted-foreground">
            Autenticación en dos pasos y gestión de sesión.
          </p>
        </div>
      </header>

      {/* MFA box */}
      <div className="relative overflow-hidden rounded-xl border border-emerald-200/60 bg-emerald-50/50 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/20">
        <div
          aria-hidden
          className="absolute right-0 top-0 h-20 w-20 -translate-y-6 translate-x-6 rounded-full bg-emerald-500/10 blur-2xl"
        />
        <div className="relative flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm">
            <Check className="h-4 w-4" strokeWidth={3} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13.5px] font-medium text-foreground">MFA activado</p>
            <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
              Cada ingreso requiere el código de 6 dígitos de tu autenticador.
            </p>
          </div>
        </div>
      </div>

      {/* Help copy */}
      <p className="text-[12px] leading-relaxed text-muted-foreground">
        ¿Perdiste tu segundo factor?{" "}
        <a
          href="mailto:soporte@arya.pe?subject=Reset%20de%20MFA%20backoffice"
          className="font-medium text-foreground/80 underline-offset-4 transition-colors hover:text-foreground hover:underline"
        >
          Pide un reset a un super admin
        </a>
        .
      </p>

      <Separator className="opacity-60" />

      <Button
        type="button"
        variant="outline"
        onClick={onSignOut}
        disabled={signingOut}
        className="w-full justify-center text-destructive hover:bg-destructive/5 hover:text-destructive"
      >
        <LogOut className="h-4 w-4" />
        {signingOut ? "Cerrando sesión…" : "Cerrar sesión"}
      </Button>
    </section>
  );
}

/* ────────────────── Permisos agrupados ────────────────── */

interface PermissionGroup {
  key: string;
  label: string;
  icon: React.ReactNode;
  permissions: Permission[];
}

const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  {
    key: "panel",
    label: "Panel",
    icon: <LayoutDashboard className="h-3.5 w-3.5" />,
    permissions: [Permission.DashboardRead],
  },
  {
    key: "contenido",
    label: "Contenido",
    icon: <BookOpen className="h-3.5 w-3.5" />,
    permissions: [
      Permission.AdviceRead,
      Permission.AdviceWrite,
      Permission.PopupsRead,
      Permission.PopupsWrite,
      Permission.ContentUpload,
    ],
  },
  {
    key: "comunidad",
    label: "Comunidad",
    icon: <UsersRound className="h-3.5 w-3.5" />,
    permissions: [Permission.CircleRead],
  },
  {
    key: "operacion",
    label: "Operación",
    icon: <Users className="h-3.5 w-3.5" />,
    permissions: [Permission.AdminsManage],
  },
  {
    key: "auditoria",
    label: "Auditoría",
    icon: <ScrollText className="h-3.5 w-3.5" />,
    permissions: [Permission.AuditRead],
  },
];

function PermissionsCard({ admin }: { admin: Admin }) {
  const owned = useMemo(() => new Set(admin.permissions), [admin.permissions]);
  const total = admin.permissions.length;

  return (
    <section className="rounded-2xl border bg-card p-6 shadow-card-premium">
      <header className="mb-5 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            aria-hidden
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15"
          >
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-[1.15rem] font-medium tracking-display-tight text-foreground">
              Scope de permisos
            </h2>
            <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted-foreground">
              Capacidades asignadas a tu rol.
            </p>
          </div>
        </div>
        <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
          <span className="text-foreground">{total}</span>{" "}
          <span className="opacity-70">de {Object.keys(Permission).length}</span>
        </span>
      </header>

      {total === 0 ? (
        <p className="text-[13px] text-muted-foreground">
          Sin permisos asignados. Habla con un super admin para que te habilite acceso.
        </p>
      ) : (
        <div className="space-y-5">
          {PERMISSION_GROUPS.map((group) => {
            const granted = group.permissions.filter((p) => owned.has(p));
            if (granted.length === 0) return null;
            return <PermissionGroupBlock key={group.key} group={group} granted={granted} />;
          })}
        </div>
      )}
    </section>
  );
}

function PermissionGroupBlock({
  group,
  granted,
}: {
  group: PermissionGroup;
  granted: Permission[];
}) {
  return (
    <div className="space-y-2">
      <h3 className="inline-flex items-center gap-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-primary">
        {group.icon}
        {group.label}
      </h3>
      <ul className="grid gap-x-5 gap-y-1.5 sm:grid-cols-2">
        {granted.map((p) => (
          <li key={p} className="flex items-start gap-2 text-[13px] text-foreground">
            <Check
              aria-hidden
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
              strokeWidth={3}
            />
            <span>{PERMISSION_LABELS[p] ?? p}</span>
            <code className="ml-auto hidden font-mono text-[10.5px] text-muted-foreground sm:inline">
              {p}
            </code>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ────────────────── Signature line ────────────────── */

function SignatureLine({ admin }: { admin: Admin }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(admin.id);
      setCopied(true);
      toast.success("ID copiado");
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar.");
    }
  };
  const createdAt = admin.created_at ? new Date(admin.created_at) : null;

  return (
    <footer className="flex flex-col items-start justify-between gap-3 border-t border-border/60 pt-5 text-[11.5px] text-muted-foreground sm:flex-row sm:items-center">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono">
        <span className="inline-flex items-center gap-1.5">
          <span className="text-[9.5px] uppercase tracking-[0.22em] opacity-70">ID</span>
          <button
            type="button"
            onClick={copy}
            className={cn(
              "group -mx-1.5 inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5",
              "text-foreground/80 transition-colors hover:bg-accent/40 hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
            aria-label={`Copiar ID ${admin.id}`}
          >
            <code className="tabular-nums">
              {admin.id.slice(0, 8)}…{admin.id.slice(-4)}
            </code>
            {copied ? (
              <Check className="h-3 w-3 text-primary" />
            ) : (
              <Copy className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
            )}
          </button>
        </span>
        {createdAt ? (
          <>
            <span aria-hidden className="opacity-50">
              ·
            </span>
            <span>
              Autorizada desde{" "}
              <span className="text-foreground/80">
                {format(createdAt, "d 'de' MMMM, yyyy", { locale: es })}
              </span>
              <span className="opacity-70">
                {" "}
                (hace {formatDistanceToNow(createdAt, { locale: es })})
              </span>
            </span>
          </>
        ) : null}
      </div>
      <span className="font-mono text-[10.5px] tracking-wide opacity-60">Arya · Backoffice</span>
    </footer>
  );
}

/* ────────────────── Utils ────────────────── */

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
